import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { API_BASE_URL } from '../config/config';
import { CourseClient, CourseMinifiedDTO } from '../services/apiClient';
import publicAxiosInstance from '../services/publicAxiosInstance';
import { isValidGuid } from '../utils/validateGuidUtil';

// Through publicAxiosInstance, not axiosInstance: this page is public, and a visitor with a stale
// session on this browser must see the course, not be sent to /login by a failed token refresh.
const courseClient = new CourseClient(API_BASE_URL, publicAxiosInstance);

// One course for the public profile page (/courses/:id). The endpoint is whitelisted in
// SecuredMiddleware, so this works signed out: a shared Courses Played list links here. Null
// when the course is not on the map (deleted, inactive, or no coordinates), and for an id that is
// not a GUID at all, which is answered without a request. Either way the page says the course
// could not be found rather than showing an error.
export const useCourseProfile = (
  id: string | undefined,
): UseQueryResult<CourseMinifiedDTO | null> => {
  const courseId = id?.trim() ?? '';
  const isValid = isValidGuid(courseId);

  return useQuery<CourseMinifiedDTO | null>({
    queryKey: ['courseProfile', courseId.toLowerCase()],
    queryFn: async () => {
      if (!isValid) {
        return null;
      }

      try {
        const response = await courseClient.getCourseProfile(courseId);
        return response?.data ?? null;
      } catch (error: unknown) {
        const status = (error as { status?: number })?.status;
        if (status === 404) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};
